import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  IconButton,
  Typography,
  Link,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LocalText from "./LocalText";

const MediaPreviewOverlay = ({ open, onClose, type, url }) => {
  const renderContent = () => {
    if (!url) {
      return (
        <Box sx={{ color: "grey.400", textAlign: "center", p: 4 }}>
          <Typography><LocalText>No URL provided.</LocalText></Typography>
        </Box>
      );
    }

    switch (type) {
      case "image":
        return (
          <Box
            component="img"
            src={url}
            alt="Preview"
            onError={(e) => {
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "block";
            }}
            sx={{ maxWidth: "100%", maxHeight: "65vh", objectFit: "contain", display: "block", mx: "auto" }}
          />
        );

      case "video":
        return (
          <Box component="video" controls src={url} sx={{ maxWidth: "100%", maxHeight: "65vh", display: "block", mx: "auto" }}>
            <LocalText>Your browser does not support the video tag.</LocalText>
          </Box>
        );

      case "audio":
        return (
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, py: 6 }}>
            <MusicNoteIcon sx={{ fontSize: 64, color: "primary.main" }} />
            <audio controls src={url} style={{ width: 300 }} />
          </Box>
        );

      case "link":
        return (
          <Box sx={{ position: "relative", width: "100%", height: { xs: '45vh', sm: '65vh' } }}>
            <iframe
              src={url}
              title="Web Preview"
              style={{ width: "100%", height: "100%", border: "none", borderRadius: 8 }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: 12,
                right: 12,
                bgcolor: "rgba(0,0,0,0.6)",
                borderRadius: 2,
                px: 1.5,
                py: 0.5,
              }}
            >
              <Link href={url} target="_blank" rel="noreferrer" sx={{ color: "white", display: "flex", alignItems: "center", gap: 0.5, fontSize: 13 }}>
                <OpenInNewIcon fontSize="small" />
                <LocalText>Open in new tab</LocalText>
              </Link>
            </Box>
          </Box>
        );

      default:
        return (
          <Typography sx={{ color: "grey.500", p: 4, textAlign: "center" }}>
            <LocalText>Unknown media type.</LocalText>
          </Typography>
        );
    }
  };

  const label = url ? url.substring(0, 60) + (url.length > 60 ? "…" : "") : "";

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          bgcolor: "#111",
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
      BackdropProps={{ sx: { backdropFilter: "blur(6px)" } }}
    >
      <DialogTitle
        sx={{
          bgcolor: "#1a1a1a",
          color: "grey.300",
          fontSize: 13,
          fontWeight: 400,
          pr: 6,
          py: 1.5,
          borderBottom: "1px solid #333",
        }}
      >
        {label}
        <IconButton
          onClick={(e) => { e.currentTarget.blur(); onClose(); }}
          size="small"
          sx={{ position: "absolute", right: 8, top: 8, color: "grey.400" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ bgcolor: "#111", p: 3 }}>
        {renderContent()}
        {type === "image" && (
          <Box sx={{ display: "none", color: "grey.400", textAlign: "center", p: 4 }}>
            <Typography><LocalText>Could not load image. Check the URL.</LocalText></Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MediaPreviewOverlay;
